import React from 'react';

const UsageAnalytics = ({ apiKeys }) => {
  // Calculate summary statistics
  const totalKeys = apiKeys?.length || 0;
  const activeKeys = apiKeys?.filter(key => key?.status === 'active')?.length || 0;
  const inactiveKeys = apiKeys?.filter(key => key?.status === 'inactive')?.length || 0;
  const revokedKeys = apiKeys?.filter(key => key?.status === 'revoked')?.length || 0;
  
  // Calculate expiring keys (within 30 days)
  const expiringKeys = apiKeys?.filter(key => {
    if (!key?.expires_at) return false;
    const expirationDate = new Date(key?.expires_at);
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expirationDate <= thirtyDaysFromNow;
  })?.length || 0;

  // Calculate usage statistics
  const recentlyUsedKeys = apiKeys?.filter(key => {
    if (!key?.last_used_at) return false;
    const lastUsed = new Date(key?.last_used_at);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return lastUsed >= sevenDaysAgo;
  })?.length || 0;

  const getStatusColor = (status, variant = 'bg') => {
    const colors = {
      total: variant === 'bg' ? 'bg-blue-50' : 'text-blue-600',
      active: variant === 'bg' ? 'bg-green-50' : 'text-green-600',
      inactive: variant === 'bg' ? 'bg-yellow-50' : 'text-yellow-600',
      revoked: variant === 'bg' ? 'bg-red-50' : 'text-red-600',
      expiring: variant === 'bg' ? 'bg-orange-50' : 'text-orange-600',
      recent: variant === 'bg' ? 'bg-purple-50' : 'text-purple-600'
    };
    return colors?.[status] || colors?.total;
  };

  const getStatusIcon = (status) => {
    const icons = {
      total: '🔑',
      active: '✅',
      inactive: '⏸️',
      revoked: '🚫',
      expiring: '⚠️',
      recent: '🕒'
    };
    return icons?.[status] || '📊';
  };

  const analyticsCards = [
    {
      id: 'total',
      label: 'Total Keys',
      value: totalKeys,
      description: 'All API keys'
    },
    {
      id: 'active',
      label: 'Active Keys',
      value: activeKeys,
      description: 'Currently functional'
    },
    {
      id: 'inactive',
      label: 'Inactive Keys',
      value: inactiveKeys,
      description: 'Temporarily disabled'
    },
    {
      id: 'revoked',
      label: 'Revoked Keys',
      value: revokedKeys,
      description: 'Permanently disabled'
    },
    {
      id: 'expiring',
      label: 'Expiring Soon',
      value: expiringKeys,
      description: 'Within 30 days'
    },
    {
      id: 'recent',
      label: 'Recently Used',
      value: recentlyUsedKeys,
      description: 'Used in last 7 days'
    }
  ];

  if (totalKeys === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">API Keys Overview</h2>
        <div className="text-sm text-gray-500">
          Last updated: {new Date()?.toLocaleTimeString()}
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {analyticsCards?.map((card) => (
          <div key={card?.id} className={`${getStatusColor(card?.id, 'bg')} rounded-lg p-4 border border-gray-200`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg">{getStatusIcon(card?.id)}</span>
              <span className={`text-2xl font-bold ${getStatusColor(card?.id, 'text')}`}>
                {card?.value}
              </span>
            </div>
            <div>
              <p className={`text-sm font-medium ${getStatusColor(card?.id, 'text')}`}>
                {card?.label}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {card?.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts for important statuses */}
      <div className="mt-4 space-y-2">
        {expiringKeys > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-orange-600 text-sm mt-0.5">⚠️</span>
              <div>
                <p className="text-orange-800 text-sm font-medium">
                  {expiringKeys} API key{expiringKeys > 1 ? 's' : ''} expiring soon
                </p>
                <p className="text-orange-700 text-xs">
                  Review and renew expiring keys to prevent service interruption
                </p>
              </div>
            </div>
          </div>
        )}
        
        {revokedKeys > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-red-600 text-sm mt-0.5">🚫</span>
              <div>
                <p className="text-red-800 text-sm font-medium">
                  {revokedKeys} revoked key{revokedKeys > 1 ? 's' : ''}
                </p>
                <p className="text-red-700 text-xs">
                  Consider cleaning up old revoked keys to keep your workspace organized
                </p>
              </div>
            </div>
          </div>
        )}

        {activeKeys === 0 && totalKeys > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-yellow-600 text-sm mt-0.5">⚠️</span>
              <div>
                <p className="text-yellow-800 text-sm font-medium">No active API keys</p>
                <p className="text-yellow-700 text-xs">
                  Activate at least one key to start using the API
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageAnalytics;